import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FileDown, NotebookPen, Pencil, Plus, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { ACTIVITY_TYPES } from './-care-log-edit-shared'
import {
    useCareLogAreaFilter,
    useCareLogAreaFilterOptions,
} from './-care-log-area-filter'
import { CareLogAreaMapDialog } from './-care-log-area-map-dialog'
import {
    careLogAmountLabel,
    careLogCardAreas,
    careLogCardMaterials,
    careLogDetail,
    careLogMediaItems,
    formatCareLogDate,
} from './-care-log-card-mapping'
import { CareLogDetailSheet } from './-care-log-detail-sheet'
import { CareLogListCard } from './-care-log-list-card'
import {
    CareLogAmountCell,
    CareLogEvidenceCell,
    CareLogParcelCell,
} from './-care-log-table-cells'
import type { CareLogCardArea } from './-care-log-list-card'
import type { CareLogActivityType } from '@/lib/api/care-logs-api'
import type { MediaItem } from '@/components/common/media-lightbox'
import type { FilterOption } from '@/components/common/filters/select-filter'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { MediaLightbox } from '@/components/common/media-lightbox'
import { defineListFilters } from '@/components/common/filters/core'
import { growerFilter } from '@/components/common/filters/grower-filter'
import { remoteSelectFilter } from '@/components/common/filters/remote-select-filter'
import { selectFilter } from '@/components/common/filters/select-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { useGrower } from '@/hooks/use-growers-query'
import { useCareLogAreaFilterTracking } from '@/hooks/use-care-log-list-tracking'
import {
    useCareLog,
    useCareLogs,
    useDeleteCareLog,
    useExportCareLogs,
} from '@/hooks/use-care-logs-query'
import { m } from '@/lib/paraglide/messages'

const ALL = 'all'

/** Số cột của bảng desktop — dùng cho `colSpan` của dòng trống/đang tải. */
const TABLE_COLS = 7

/** Cỡ trang danh sách — cùng cỡ với danh sách đơn hàng (`ORDERS_PER_PAGE`). */
const CARE_LOGS_PER_PAGE = 20

const activityLabel: Record<CareLogActivityType, () => string> = {
    fertilizing: m.care_log_activity_fertilizing,
    harvesting: m.care_log_activity_harvesting,
    replanting: m.care_log_activity_replanting,
    spraying: m.care_log_activity_spraying,
    weeding: m.care_log_activity_weeding,
    watering: m.care_log_activity_watering,
    pruning: m.care_log_activity_pruning,
    bud_picking: m.care_log_activity_bud_picking,
    other: m.care_log_activity_other,
}

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * `grower` chỉ dành cho admin: tài khoản hộ dân đã bị giới hạn phạm vi ở server
 * và cũng không có quyền `growers.read` mà ô chọn hộ cần — render ra cho họ là
 * bắn request 403. Màn ẩn nó qua `hiddenFields` của `useListFilters`; config
 * vẫn khai đủ để `?grower=` trong link admin gửi là param hợp lệ.
 *
 * `area` là vùng trồng, lựa chọn phụ thuộc hộ: admin phải chọn hộ trước (ô vùng
 * ẩn tới lúc đó), tài khoản hộ thấy vùng của mọi hộ mình.
 */
const FILTERS = defineListFilters({
    grower: growerFilter({
        label: m.care_log_grower,
        placeholder: m.care_log_filter_all_growers,
    }),
    area: remoteSelectFilter({
        label: m.care_log_parcels,
        allLabel: m.care_log_filter_all_areas,
        // Hộ đang lọc lấy từ search của CHÍNH route này (không useSearch rời).
        useOptions: (): FilterOption<string>[] =>
            useCareLogAreaFilterOptions(Route.useSearch().grower),
    }),
    activity_type: selectFilter<string>({
        label: m.care_log_activity_type,
        defaultValue: ALL,
        options: () => [
            { value: ALL, label: m.care_log_filter_all_activities() },
            ...ACTIVITY_TYPES.map((at) => ({
                value: at,
                label: activityLabel[at](),
            })),
        ],
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/care-logs/',
)({
    component: CareLogsPage,
    validateSearch: FILTERS.validateSearch,
})

export function CareLogsPage() {
    const navigate = useNavigate()
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('care_logs.write')
    const canDelete = hasPermission('care_logs.delete')
    // Only admins pick a household to filter by; a grower account is already
    // scoped server-side to its own households (and lacks growers.read that the
    // household picker needs).
    const isAdmin = hasPermission('care_logs.manage_all')

    const urlSearch = Route.useSearch()

    // Chip lọc theo hộ trồng cần TÊN, URL chỉ có id → tra runtime rồi truyền.
    const selectedGrowerId =
        isAdmin && typeof urlSearch.grower === 'string'
            ? urlSearch.grower
            : undefined
    const { data: selectedGrower } = useGrower(selectedGrowerId)
    // Chip vùng cũng cần tên — tra từ đúng danh sách ô lọc đang hiện.
    const areaFilter = useCareLogAreaFilter(urlSearch.grower)
    const urlArea = typeof urlSearch.area === 'string' ? urlSearch.area : ''
    const selectedArea = areaFilter.options.find((o) => o.value === urlArea)
    // Admin chưa chọn hộ thì không có danh sách vùng để chọn → ẩn hẳn ô vùng
    // (ẩn cũng ép giá trị về mặc định, link cũ có `?area=` không lọc ngầm).
    const hiddenFields: ('grower' | 'area')[] = isAdmin
        ? selectedGrowerId
            ? []
            : ['area']
        : ['grower']

    // `?area=` không thuộc danh sách (vùng đã gỡ khỏi hộ, link của tài khoản
    // khác): ô chọn sẽ trống, chip không có mà danh sách vẫn bị lọc rỗng. Bỏ nó
    // đi — nhưng CHỈ khi danh sách đã tải xong, kẻo xoá nhầm vùng hợp lệ.
    const staleArea =
        !!urlArea &&
        areaFilter.isReady &&
        !hiddenFields.includes('area') &&
        !selectedArea
    useCareLogAreaFilterTracking({
        areaId: hiddenFields.includes('area') ? '' : urlArea,
        isKnownArea: !!selectedArea,
        isAdmin,
    })
    useEffect(() => {
        if (!staleArea) return
        void navigate({
            to: '/agriculture/care-logs',
            search: (prev: Record<string, unknown>) => ({
                ...prev,
                area: undefined,
                // Bỏ vùng là đổi bộ lọc → về trang 1 như mọi lần đổi lọc khác.
                page: undefined,
            }),
            replace: true,
        })
    }, [staleArea, navigate])

    const {
        values: filters,
        barProps,
        page,
        setPage,
    } = useListFilters({
        def: FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                to: '/agriculture/care-logs',
                // Vùng thuộc về hộ: đổi hộ thì bỏ vùng cũ, kẻo lọc theo một
                // vùng của hộ khác mà ra danh sách rỗng khó hiểu.
                // So với hộ ĐANG CÓ HIỆU LỰC, không so `?grower=` thô: tài
                // khoản hộ có ô hộ bị ẩn nên `next.grower` luôn rỗng dù URL còn
                // `?grower=` cũ — so thô là mọi thao tác đều xoá mất vùng.
                search: () =>
                    next.grower === selectedGrowerId
                        ? next
                        : { ...next, area: undefined },
                replace: opts?.replace ?? false,
            }),
        columns: 4,
        hiddenFields,
        chipLabels: {
            grower: selectedGrower
                ? `${m.care_log_grower()}: ${selectedGrower.name}`
                : undefined,
            area: selectedArea
                ? `${m.care_log_parcels()}: ${selectedArea.label}`
                : undefined,
        },
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    // Danh sách và xuất Excel dùng CHUNG object này để file luôn khớp màn hình.
    const filterParams = {
        grower_id: filters.grower || undefined,
        area_id: filters.area === ALL ? undefined : filters.area,
        activity_type:
            filters.activity_type === ALL ? undefined : filters.activity_type,
    }
    // Phân trang giống danh sách đơn hàng: `?page=` trên URL, đổi bộ lọc tự về
    // trang 1 (useListFilters lo). Xuất Excel vẫn dùng `filterParams` — file
    // lấy đủ mọi trang chứ không riêng trang đang xem.
    const { data, isLoading, isFetching, refetch } = useCareLogs({
        ...filterParams,
        page,
        per_page: CARE_LOGS_PER_PAGE,
    })
    const exportExcel = useExportCareLogs()
    const rows = data?.data ?? []
    const meta = data?.meta

    // Trang đang xem vượt số trang (xoá dòng cuối của trang cuối, link cũ có
    // `?page=` lớn): lùi về trang cuối còn dữ liệu. Không thì kẹt ở trang trống
    // báo "chưa có nhật ký" mà thanh phân trang đã ẩn, không còn nút để quay về.
    // Chờ tải xong để không nhảy theo số liệu cũ trong cache.
    const lastPage = meta ? Math.max(meta.total_pages, 1) : page
    const pageOverflow = !isFetching && page > lastPage
    useEffect(() => {
        if (!pageOverflow) return
        void navigate({
            to: '/agriculture/care-logs',
            search: (prev: Record<string, unknown>) => ({
                ...prev,
                page: lastPage > 1 ? lastPage : undefined,
            }),
            replace: true,
        })
    }, [pageOverflow, lastPage, navigate])

    const deleteMut = useDeleteCareLog()
    const deletion = useWorkflowTracking('care_log', {
        autoStart: false,
        operation: 'delete',
    })
    const deleteTarget = useRef('')

    // Card mở CHI TIẾT chứ không nhảy thẳng vào màn sửa: hộ dân xem lại đã bón
    // gì rồi mới quyết định sửa hay xoá. Giữ id thay vì cả dòng để dữ liệu luôn
    // theo kịp lần refetch gần nhất.
    const [detailId, setDetailId] = useState<string | null>(null)
    const [mapAreas, setMapAreas] = useState<CareLogCardArea[] | null>(null)
    // Một popup ảnh/video cho cả bảng: mở ra thì nạp đúng bộ tệp của dòng vừa
    // bấm, nên next/prev chạy trong phạm vi một nhật ký chứ không nhảy sang
    // nhật ký khác.
    const [media, setMedia] = useState<{
        items: MediaItem[]
        index: number
    } | null>(null)
    const openMedia = (items: MediaItem[], index: number) =>
        setMedia({ items, index })
    const detailRow = rows.find((r) => r.id === detailId) ?? null
    // Danh sách đã trả sẵn ảnh minh chứng nên chi tiết mở ra là có ngay; request
    // chi tiết vẫn giữ để lấy các trường chỉ endpoint đó có và làm mới tệp nếu
    // dòng trong cache đã cũ.
    const { data: detailFull } = useCareLog(detailId)

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {barProps.activeCount > 0
                    ? m.list_empty_filtered()
                    : m.care_log_empty()}
            </span>
            {barProps.activeCount > 0 ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    const openEdit = (id: string) =>
        void navigate({
            to: '/agriculture/care-logs/$id/edit',
            params: { id },
        })
    const confirmDelete = async (id: string) => {
        if (!window.confirm(m.care_log_delete_confirm())) return
        if (deleteTarget.current !== id) deletion.finish('closed')
        deleteTarget.current = id
        try {
            await deletion.run(() => deleteMut.mutateAsync(id))
        } catch {
            /* Existing hook reports errors. */
        }
    }

    /** Nút sửa/xoá dùng chung cho card mobile và hàng bảng desktop. */
    const rowActions = (id: string) => (
        <>
            {canWrite && (
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label={m.care_log_edit()}
                    onClick={() => openEdit(id)}
                >
                    <Pencil className="size-4" />
                </Button>
            )}
            {canDelete && (
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label={m.care_log_delete()}
                    onClick={() => confirmDelete(id)}
                >
                    <Trash2 className="size-4" />
                </Button>
            )}
        </>
    )

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<NotebookPen className="size-5 shrink-0" />}
                title={m.care_log_title()}
                primaryActions={
                    <>
                        {/* Chỉ khoá khi đang tải file (chống bấm đúp). */}
                        <Button
                            variant="outline"
                            className="w-full"
                            disabled={exportExcel.isPending}
                            onClick={() => exportExcel.mutate(filterParams)}
                        >
                            <FileDown className="mr-1 size-4" />
                            {m.care_log_export_excel()}
                        </Button>
                        {canWrite ? (
                            <Button
                                className="w-full"
                                onClick={() =>
                                    void navigate({
                                        to: '/agriculture/care-logs/new',
                                    })
                                }
                            >
                                <Plus className="mr-1 size-4" />
                                {m.care_log_new()}
                            </Button>
                        ) : null}
                    </>
                }
            />

            <ListFilterBar
                {...barProps}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/* Mobile: card list — bảng 5 cột bị bóp/cuộn ngang trên điện thoại,
                hộ dân khó bấm đúng nút. Lấy mẫu từ màn danh sách lô. */}
            <div className="space-y-2 md:hidden">
                {isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.care_log_loading()}
                    </div>
                ) : rows.length === 0 ? (
                    emptyState
                ) : (
                    rows.map((row) => (
                        <CareLogListCard
                            key={row.id}
                            grower={row.grower_name || '—'}
                            logDate={formatCareLogDate(row.log_date)}
                            activity={
                                activityLabel[row.activity_type]?.() ??
                                row.activity_type
                            }
                            quantity={row.quantity}
                            showQuantity={false}
                            areas={careLogCardAreas(row)}
                            materials={careLogCardMaterials(row)}
                            amountLabel={careLogAmountLabel(row.activity_type)}
                            onOpenMap={() => setMapAreas(careLogCardAreas(row))}
                            onClick={() => setDetailId(row.id)}
                        />
                    ))
                )}
            </div>

            {/* Desktop: bảng */}
            <div className="hidden overflow-x-auto rounded-md border bg-card md:block">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.care_log_log_date()}</TableHead>
                            <TableHead>{m.care_log_grower()}</TableHead>
                            <TableHead>{m.care_log_activity_type()}</TableHead>
                            <TableHead>{m.care_log_col_parcel()}</TableHead>
                            <TableHead>{m.care_log_quantity()}</TableHead>
                            <TableHead>{m.care_log_evidence()}</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={TABLE_COLS}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.care_log_loading()}
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={TABLE_COLS}>
                                    {emptyState}
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell className="whitespace-nowrap tabular-nums">
                                        {formatCareLogDate(row.log_date)}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {row.grower_name || '—'}
                                    </TableCell>
                                    <TableCell>
                                        {activityLabel[row.activity_type]?.() ??
                                            row.activity_type}
                                    </TableCell>
                                    <TableCell className="align-top">
                                        <CareLogParcelCell
                                            areas={careLogCardAreas(row)}
                                        />
                                    </TableCell>
                                    {/* Định lượng theo TỪNG vật tư, không dùng
                                        `row.quantity`: tổng của nhật ký cộng
                                        lẫn đơn vị (3 chai + 2,5 bao = 5,5) nên
                                        vô nghĩa. Hái chè cũng đi qua đây —
                                        `careLogCardMaterials` quy sản lượng
                                        thành một dòng kg. */}
                                    <TableCell className="min-w-56 align-top">
                                        <CareLogAmountCell
                                            materials={careLogCardMaterials(
                                                row,
                                            )}
                                        />
                                    </TableCell>
                                    {/* min-w-56: dải 4 ô rộng 178px, cộng
                                        padding hai bên của ô bảng là ~210px —
                                        hẹp hơn thì bảng tự co cột ảnh và ô "+N"
                                        rơi xuống hàng hai, đúng thứ giới hạn số
                                        ô sinh ra để tránh. */}
                                    <TableCell className="min-w-56 align-top">
                                        <CareLogEvidenceCell
                                            items={careLogMediaItems(row.files)}
                                            onOpen={openMedia}
                                        />
                                    </TableCell>
                                    <TableCell className="space-x-1 text-right align-top">
                                        {rowActions(row.id)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — cùng bố cục với danh sách đơn hàng. */}
            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.care_log_total_count({ count: String(meta.total) })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            {m.common_pagination_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage(page + 1)}
                        >
                            {m.common_pagination_next()}
                        </Button>
                    </div>
                </div>
            )}

            <CareLogDetailSheet
                log={
                    detailRow
                        ? {
                              ...careLogDetail(detailRow, detailFull),
                              activity:
                                  activityLabel[detailRow.activity_type]?.() ??
                                  detailRow.activity_type,
                          }
                        : null
                }
                onOpenChange={(open) => !open && setDetailId(null)}
                canWrite={canWrite}
                canDelete={canDelete}
                onEdit={(id) => {
                    setDetailId(null)
                    openEdit(id)
                }}
                onDelete={(id) => {
                    setDetailId(null)
                    confirmDelete(id)
                }}
                onOpenMap={() =>
                    setMapAreas(detailRow ? careLogCardAreas(detailRow) : [])
                }
            />
            <CareLogAreaMapDialog
                open={!!mapAreas}
                onOpenChange={(open) => !open && setMapAreas(null)}
                areas={mapAreas ?? []}
            />
            <MediaLightbox
                items={media?.items ?? []}
                index={media?.index ?? null}
                onIndexChange={(index) =>
                    setMedia((prev) => (prev ? { ...prev, index } : prev))
                }
                onClose={() => setMedia(null)}
            />
        </div>
    )
}
